import React, { useState, useEffect } from 'react';
import { Supplier, CreateSupplierFormData } from '../../types/supplier';
import {
  TruckIcon,
  UserIcon,
  BuildingOffice2Icon,
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon
} from '@heroicons/react/24/outline';
import FormTabs, { FormSection as FormCard } from '../common/FormTabs';
import Modal from '../common/Modal';
import toast from 'react-hot-toast';

interface SupplierFormProps {
  supplier?: Supplier | null;
  onSubmit: (data: CreateSupplierFormData) => void;
  onCancel: () => void;
  loading?: boolean;
}

interface FormSection {
  title: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  color: string;
}

const FORM_SECTIONS: Record<string, FormSection> = {
  basic: {
    title: 'Alapadatok',
    icon: TruckIcon,
    color: 'from-blue-500 to-indigo-600'
  },
  contact: {
    title: 'Kapcsolattartási adatok',
    icon: EnvelopeIcon,
    color: 'from-emerald-500 to-teal-600'
  },
  address: {
    title: 'Cím adatok',
    icon: MapPinIcon,
    color: 'from-orange-500 to-yellow-600'
  }
};

const SupplierForm: React.FC<SupplierFormProps> = ({
  supplier,
  onSubmit,
  onCancel,
  loading = false
}) => {
  const [formData, setFormData] = useState<CreateSupplierFormData>({
    name: '',
    companyName: '',
    taxNumber: '',
    email: '',
    phone: '',
    address: {
      country: 'Magyarország',
      postalCode: '',
      city: '',
      street: '',
      houseNumber: ''
    },
    isActive: true,
    notes: ''
  });

  // Ha szerkesztünk, töltsük be az adatokat
  useEffect(() => {
    if (supplier) {
      setFormData({
        name: supplier.name,
        companyName: supplier.companyName || '',
        taxNumber: supplier.taxNumber || '',
        email: supplier.email || '',
        phone: supplier.phone || '',
        address: supplier.address || {
          country: 'Magyarország',
          postalCode: '',
          city: '',
          street: '',
          houseNumber: ''
        },
        isActive: supplier.isActive,
        notes: supplier.notes || ''
      });
    }
  }, [supplier]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name?.trim()) {
      setErrorTabs(['basic']);
      setActiveTab('basic');
      toast.error('A beszállító neve kötelező');
      return;
    }
    setErrorTabs([]);
    
    try {
      await onSubmit(formData);
      
      // Sikeres mentés toast
      if (supplier) {
        toast.success('Beszállító sikeresen frissítve!');
      } else {
        toast.success('Beszállító sikeresen létrehozva!');
      }
    } catch (error) {
      console.error('Error submitting supplier:', error);
      toast.error('Hiba történt a beszállító mentése során');
    }
  };

  type Tab = 'basic' | 'contact' | 'address';
  const [activeTab, setActiveTab] = useState<Tab>('basic');
  const [errorTabs, setErrorTabs] = useState<Tab[]>([]);
  const tabCls = (tab: Tab) => (activeTab === tab ? 'space-y-4' : 'hidden');

  const handleChange = (field: string, value: any) => {
    if (field.startsWith('address.')) {
      const addressField = field.replace('address.', '');
      setFormData(prev => ({
        ...prev,
        address: {
          ...prev.address,
          [addressField]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  return (
    <Modal
      isOpen={true}
      onClose={onCancel}
      title={supplier ? 'Beszállító szerkesztése' : 'Új beszállító'}
      size="4xl"
    >
      <form onSubmit={handleSubmit} className="space-y-4 pb-24 sm:pb-0">
        <FormTabs
          tabs={[
            { id: 'basic', label: 'Alapadatok', icon: FORM_SECTIONS.basic.icon },
            { id: 'contact', label: 'Elérhetőség', icon: FORM_SECTIONS.contact.icon },
            { id: 'address', label: 'Cím', icon: FORM_SECTIONS.address.icon },
          ]}
          active={activeTab}
          onChange={(id) => setActiveTab(id as Tab)}
          errorTabs={errorTabs}
        />

        <div className="sm:min-h-[44vh]">
        <div data-tab="basic" className={tabCls('basic')}>
        {/* Alapadatok szekció */}
        <FormCard title={FORM_SECTIONS.basic.title} icon={FORM_SECTIONS.basic.icon}>
              <div className="grid grid-cols-12 gap-4">
                {/* Beszállító neve */}
                <div className="col-span-12">
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Beszállító neve *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    className="block w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm py-2 px-3 text-gray-800 dark:text-gray-200 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                    placeholder="Beszállító neve (lehet cég vagy magánszemély)"
                  />
                </div>

                {/* Cégnév */}
                <div className="col-span-12 sm:col-span-6">
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Cégnév
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={formData.companyName}
                      onChange={(e) => handleChange('companyName', e.target.value)}
                      className="block w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm py-2 pl-10 pr-3 text-gray-800 dark:text-gray-200 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                      placeholder="Cégnév (opcionális)"
                    />
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <BuildingOffice2Icon className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                    </div>
                  </div>
                </div>

                {/* Adószám */}
                <div className="col-span-12 sm:col-span-6">
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Adószám
                  </label>
                  <input
                    type="text"
                    value={formData.taxNumber}
                    onChange={(e) => handleChange('taxNumber', e.target.value)}
                    className="block w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm py-2 px-3 text-gray-800 dark:text-gray-200 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                    placeholder="12345678-1-23 (opcionális)"
                  />
                </div>

                {/* Státusz */}
                <div className="col-span-12">
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Státusz</label>
                  <div className="mt-1.5">
                    <label className="inline-flex items-center bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 cursor-pointer transition-colors hover:border-primary-300 dark:hover:border-primary-400">
                      <input
                        type="checkbox"
                        checked={formData.isActive}
                        onChange={(e) => handleChange('isActive', e.target.checked)}
                        className="h-4 w-4 text-primary-600 border-gray-300 dark:border-gray-600 rounded focus:ring-primary-500"
                      />
                      <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Aktív beszállító</span>
                    </label>
                  </div>
                </div>
              </div>
        </FormCard>

        {/* Megjegyzések */}
        <FormCard title="Megjegyzések">
          <textarea
            value={formData.notes}
            onChange={(e) => handleChange('notes', e.target.value)}
            rows={3}
            className="block w-full border rounded-lg text-sm py-2 px-3"
            placeholder="További megjegyzések..."
          />
        </FormCard>
        </div>

        <div data-tab="contact" className={tabCls('contact')}>
        {/* Kapcsolattartási adatok szekció */}
        <FormCard title={FORM_SECTIONS.contact.title} icon={FORM_SECTIONS.contact.icon}>
              <div className="grid grid-cols-12 gap-4">
                {/* Email */}
                <div className="col-span-12 sm:col-span-6">
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Email
                  </label>
                  <div className="relative">
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      className="block w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm py-2 pl-10 pr-3 text-gray-800 dark:text-gray-200 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                      placeholder="email@example.com (opcionális)"
                    />
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <EnvelopeIcon className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                    </div>
                  </div>
                </div>

                {/* Telefon */}
                <div className="col-span-12 sm:col-span-6">
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Telefon
                  </label>
                  <div className="relative">
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => handleChange('phone', e.target.value)}
                      className="block w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm py-2 pl-10 pr-3 text-gray-800 dark:text-gray-200 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                      placeholder="+36 1 234 5678 (opcionális)"
                    />
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <PhoneIcon className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                    </div>
                  </div>
                </div>
              </div>
        </FormCard>
        </div>

        <div data-tab="address" className={tabCls('address')}>
        {/* Cím szekció */}
        <FormCard title={FORM_SECTIONS.address.title} icon={FORM_SECTIONS.address.icon}>
              <div className="grid grid-cols-12 gap-4">
                {/* Ország */}
                <div className="col-span-12 sm:col-span-6">
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Ország
                  </label>
                  <input
                    type="text"
                    value={formData.address?.country || ''}
                    onChange={(e) => handleChange('address.country', e.target.value)}
                    className="block w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm py-2 px-3 text-gray-800 dark:text-gray-200 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                    placeholder="Magyarország (opcionális)"
                  />
                </div>

                {/* Irányítószám */}
                <div className="col-span-12 sm:col-span-3">
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Irányítószám
                  </label>
                  <input
                    type="text"
                    value={formData.address?.postalCode || ''}
                    onChange={(e) => handleChange('address.postalCode', e.target.value)}
                    className="block w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm py-2 px-3 text-gray-800 dark:text-gray-200 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                    placeholder="1234 (opcionális)"
                  />
                </div>

                {/* Házszám */}
                <div className="col-span-12 sm:col-span-3">
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Házszám
                  </label>
                  <input
                    type="text"
                    value={formData.address?.houseNumber || ''}
                    onChange={(e) => handleChange('address.houseNumber', e.target.value)}
                    className="block w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm py-2 px-3 text-gray-800 dark:text-gray-200 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                    placeholder="12 (opcionális)"
                  />
                </div>

                {/* Város */}
                <div className="col-span-12 sm:col-span-6">
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Város
                  </label>
                  <input
                    type="text"
                    value={formData.address?.city || ''}
                    onChange={(e) => handleChange('address.city', e.target.value)}
                    className="block w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm py-2 px-3 text-gray-800 dark:text-gray-200 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                    placeholder="Budapest (opcionális)"
                  />
                </div>

                {/* Utca */}
                <div className="col-span-12 sm:col-span-6">
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Utca
                  </label>
                  <input
                    type="text"
                    value={formData.address?.street || ''}
                    onChange={(e) => handleChange('address.street', e.target.value)}
                    className="block w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm py-2 px-3 text-gray-800 dark:text-gray-200 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                    placeholder="Fő utca (opcionális)"
                  />
                </div>
              </div>
        </FormCard>
        </div>
        </div>

        {/* Gombok – mobilon alul rögzítve */}
        <div className="fixed sm:static bottom-0 left-0 right-0 z-10 bg-[var(--crm-surface)] border-t sm:border-0 border-[var(--crm-border)] p-3 sm:p-0 sm:pt-2 [padding-bottom:max(0.75rem,env(safe-area-inset-bottom))] sm:[padding-bottom:0] flex gap-2 sm:justify-end">
          <button type="button" onClick={onCancel} className="crm-btn crm-btn-secondary h-11 sm:h-9 flex-1 sm:flex-none">
            Mégse
          </button>
          <button type="submit" disabled={loading} className="crm-btn crm-btn-primary h-11 sm:h-9 flex-[2] sm:flex-none">
            {loading ? 'Mentés...' : supplier ? 'Frissítés' : 'Létrehozás'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default SupplierForm; 